import React, { memo, useRef } from 'react'

import {
  Button, Icon, Slider, Undo, Redo, Eraser, Brush, ImagesHistory,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types'

import { iconSize, BRUSH } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import HistoryMenu from './Components/HistoryMenu/HistoryMenu'

const undoIcon = <Icon svgComponent={Undo} size={iconSize} color="dark" notSpaced />
const redoIcon = <Icon svgComponent={Redo} size={iconSize} color="dark" notSpaced />
const eraserIcon = <Icon svgComponent={Eraser} size={iconSize} color="dark" notSpaced />
const brushIcon = <Icon svgComponent={Brush} size={iconSize} color="dark" notSpaced />
const historyIcon = <Icon svgComponent={ImagesHistory} size={iconSize} color="dark" notSpaced />

function Toolbar({
  canUndo,
  canRedo,
  isErasing,
  brushSize,
  versions,
  currentId = null,
  isHistoryOpen,
  isSending,
  onUndo,
  onRedo,
  onToggleEraser,
  onBrushSizeChange,
  onToggleHistory,
  onSelectVersion,
  onCloseHistory,
}) {
  const t = useTranslation()
  const historyRef = useRef(null)

  return (
    <div className="image-assistant-edit-tab__canvas-toolbar">
      <div className="image-assistant-edit-tab__tools">
        <Button
          variant="only-icon"
          type="secondary"
          size="small"
          actionType="button"
          customClass="image-assistant-edit-tab__tool"
          icon={undoIcon}
          aria-label="Undo"
          disabled={!canUndo || isSending}
          onClick={onUndo}
        />
        <Button
          variant="only-icon"
          type="secondary"
          size="small"
          actionType="button"
          customClass="image-assistant-edit-tab__tool"
          icon={redoIcon}
          aria-label="Redo"
          disabled={!canRedo || isSending}
          onClick={onRedo}
        />
        <Button
          variant="only-icon"
          type="secondary"
          size="small"
          actionType="button"
          customClass="image-assistant-edit-tab__tool"
          icon={eraserIcon}
          aria-label="Eraser"
          aria-pressed={isErasing}
          disabled={isSending}
          onClick={onToggleEraser}
        />
        <div className="image-assistant-edit-tab__brush">
          {brushIcon}
          <Slider
            hideRangeValues
            min={BRUSH.MIN}
            max={BRUSH.MAX}
            step={BRUSH.STEP}
            value={brushSize}
            onChange={onBrushSizeChange}
            isDisabled={isSending}
            wrapperClassName="image-assistant-edit-tab__brush-slider"
          />
        </div>
      </div>
      <div className="image-assistant-edit-tab__history" ref={historyRef}>
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          actionType="button"
          customClass="image-assistant-edit-tab__history-button"
          icon={historyIcon}
          aria-haspopup="menu"
          aria-expanded={isHistoryOpen}
          disabled={versions.length === 0 || isSending}
          onClick={onToggleHistory}
        >
          {t('imageAssistantPanel.editTab.toolbar.history')}
        </Button>
        {isHistoryOpen && (
          <HistoryMenu
            versions={versions}
            currentId={currentId}
            triggerRef={historyRef}
            onSelect={onSelectVersion}
            onClose={onCloseHistory}
          />
        )}
      </div>
    </div>
  )
}

Toolbar.propTypes = {
  canUndo: bool.isRequired,
  canRedo: bool.isRequired,
  isErasing: bool.isRequired,
  brushSize: number.isRequired,
  versions: arrayOf(shape({
    id: number.isRequired,
    url: string.isRequired,
    label: string.isRequired,
    kind: string,
    prompt: string,
  })).isRequired,
  currentId: number,
  isHistoryOpen: bool.isRequired,
  isSending: bool.isRequired,
  onUndo: func.isRequired,
  onRedo: func.isRequired,
  onToggleEraser: func.isRequired,
  onBrushSizeChange: func.isRequired,
  onToggleHistory: func.isRequired,
  onSelectVersion: func.isRequired,
  onCloseHistory: func.isRequired,
}

export default memo(Toolbar)
